/**
 * Проверка ответов службы на границе (D-056). Тип TypeScript ответ не проверяет:
 * декодер превращает неожиданную форму в ошибку `invalid-response`, а не в
 * пустой экран. Общие шаги для журналов чеков и доставки (UC1b).
 */

export function fail(message: string): never {
  throw new Error(message)
}

export type Wire = Readonly<Record<string, unknown>>

export function record(raw: unknown, where: string): Wire {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as Wire) : fail(`${where} — не объект`)
}

export function array(raw: unknown, where: string): readonly unknown[] {
  return Array.isArray(raw) ? (raw as readonly unknown[]) : fail(`${where} — не массив`)
}

/** Строка; пустая допустима (в чеке имя кассира может быть пустым, и это данные, а не ошибка). */
export function text(raw: unknown, where: string): string {
  return typeof raw === 'string' ? raw : fail(`${where} — не строка`)
}

export function textOrNull(raw: unknown, where: string): string | null {
  return raw === null || raw === undefined ? null : text(raw, where)
}

export function integer(raw: unknown, where: string): number {
  return typeof raw === 'number' && Number.isSafeInteger(raw) ? raw : fail(`${where} — не целое число`)
}

export function integerOrNull(raw: unknown, where: string): number | null {
  return raw === null || raw === undefined ? null : integer(raw, where)
}

export function decimal(raw: unknown, where: string): number {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : fail(`${where} — не число`)
}

export function flag(raw: unknown, where: string): boolean {
  return typeof raw === 'boolean' ? raw : fail(`${where} — не логическое`)
}

export function flagOrNull(raw: unknown, where: string): boolean | null {
  return raw === null || raw === undefined ? null : flag(raw, where)
}

/** Дата и время ответа — строка ISO 8601 с поясом; хранится строкой, как пришла (реквизиты «как есть»). */
export function instantOrNull(raw: unknown, where: string): string | null {
  if (raw === null || raw === undefined) return null
  const value = text(raw, where)
  return Number.isNaN(Date.parse(value)) ? fail(`${where} — не дата`) : value
}

export function instant(raw: unknown, where: string): string {
  return instantOrNull(raw, where) ?? fail(`${where} — нет даты`)
}

/** `ResourceId` без конвертера приходит объектом `{ value }`, с конвертером — строкой. */
export function resourceIdOf(raw: unknown, where: string): string {
  if (typeof raw === 'string' && raw !== '') return raw
  if (typeof raw === 'object' && raw !== null) {
    const value = (raw as { value?: unknown }).value
    if (typeof value === 'string' && value !== '') return value
  }
  return fail(`${where} — нет идентификатора ресурса`)
}

export function sameResource(left: string, right: string): boolean {
  return left.toLowerCase() === right.toLowerCase()
}

/**
 * Конверт `{kind, resourceId, contractVersion, schemaVersion, data}`. Ответ
 * ДРУГОГО ресурса или другого вида — ошибка, а не данные: два AQSI с одинаковыми
 * номерами чеков не должны смешаться, даже если кэш или прокси ошибся адресом.
 */
export function envelopeData(raw: unknown, kind: string, resourceId: string): unknown {
  const body = record(raw, 'ответ')
  if (body.kind !== kind) fail(`ответ вида «${String(body.kind)}», ожидался «${kind}»`)
  if (!sameResource(resourceIdOf(body.resourceId, 'resourceId'), resourceId)) {
    fail('ответ принадлежит другому ресурсу')
  }
  return body.data
}
